class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
// ===== Free Shipping Progress Bar - Live Update =====
class FreeShippingBar {
  constructor() {
    this.bar = null;
    this.threshold = 15000; // default
    this.init();
  }

  init() {
    this.bar = document.querySelector('.free-shipping-bar');
    if (!this.bar) return;

    this.threshold = parseInt(this.bar.dataset.threshold) || 15000;
    this.bindEvents();
  }

  bindEvents() {
    // Dawn cart drawer events
    document.addEventListener('cart:updated', () => this.update());
    
    // Fallback - listen for quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="updates[]"], .quantity__input')) {
        setTimeout(() => this.update(), 300);
      }
    });
  }

  async update() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.render(cart.total_price);
    } catch (e) {
      console.log('Free shipping bar update failed', e);
    }
  }

  render(cartTotal) {
    if (!this.bar) {
      this.bar = document.querySelector('.free-shipping-bar');
      if (!this.bar) return;
    }

    const remaining = this.threshold - cartTotal;
    const progress = Math.min(100, Math.round((cartTotal / this.threshold) * 100));
    const textEl = this.bar.querySelector('.free-shipping-bar__text');
    const progressEl = this.bar.querySelector('.free-shipping-bar__progress');
    const lang = document.documentElement.lang || 'en';

    if (progressEl) {
      progressEl.style.width = progress + '%';
    }

    if (!textEl) return;

    if (cartTotal >= this.threshold) {
      textEl.classList.add('free-shipping-bar__text--success');
      textEl.innerHTML = this.getSuccessText(lang);
    } else {
      textEl.classList.remove('free-shipping-bar__text--success');
      textEl.innerHTML = this.getRemainingText(lang, remaining);
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: Shopify.currency.active || 'EUR',
      minimumFractionDigits: 2
    });
  }

  getSuccessText(lang) {
    const texts = {
      sk: 'Máte dopravu <strong>zadarmo</strong>!',
      cs: 'Máte dopravu <strong>zdarma</strong>!',
      pl: 'Masz <strong>darmową dostawę</strong>!',
      ro: 'Ai <strong>livrare gratuită</strong>!',
      de: 'Du hast <strong>kostenlosen Versand</strong>!',
      hu: 'Ingyenes <strong>szállítást</strong> kaptál!',
      el: 'Έχετε <strong>δωρεάν αποστολή</strong>!',
      en: 'You\'ve unlocked <strong>free shipping</strong>!'
    };
    return texts[lang] || texts.en;
  }

  getRemainingText(lang, remaining) {
    const money = this.formatMoney(remaining);
    const texts = {
      sk: `Do dopravy zadarmo ti chýba ešte <strong>${money}</strong>`,
      cs: `Do dopravy zdarma ti chybí ještě <strong>${money}</strong>`,
      pl: `Brakuje ci jeszcze <strong>${money}</strong> do darmowej dostawy`,
      ro: `Mai ai nevoie de <strong>${money}</strong> pentru livrare gratuită`,
      de: `Dir fehlen noch <strong>${money}</strong> für kostenlosen Versand`,
      hu: `Még <strong>${money}</strong> hiányzik az ingyenes szállításhoz`,
      el: `Σας λείπουν ακόμη <strong>${money}</strong> για δωρεάν αποστολή`,
      en: `You're <strong>${money}</strong> away from free shipping`
    };
    return texts[lang] || texts.en;
  }
}

// Initialize when cart drawer is ready
document.addEventListener('DOMContentLoaded', () => {
  new FreeShippingBar();
});

// Re-initialize after cart drawer renders (Dawn specific)
document.addEventListener('cart:updated', () => {
  setTimeout(() => new FreeShippingBar(), 100);
});